import { cn } from '@/lib/utils'
import { AlertTriangle } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

export interface Widget {
  kind: 'stats' | 'bar' | 'line' | 'table'
  title: string
  note?: string
  unit?: string
  items?: Array<{ label: string; value: string; hint?: string; tone?: 'good' | 'bad' | 'warn' | 'neutral' }>
  points?: Array<{ label: string; value: number; flag?: boolean }>
  columns?: string[]
  rows?: string[][]
}

// One series per chart: ink for the data, red only for the point the agent flagged.
const INK = 'var(--dw-ink)'
const FLAG = '#ef4444'

const TONE: Record<string, string> = {
  good: 'text-emerald-600 dark:text-emerald-400',
  bad: 'text-red-600 dark:text-red-400',
  warn: 'text-amber-600 dark:text-amber-400',
  neutral: 'text-neutral-900 dark:text-white'
}

function fmt(value: number, unit = '') {
  const n = Math.abs(value) >= 1000 ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (unit === '$') return `$${n}`
  return unit ? `${n}${unit === '%' ? '%' : ` ${unit}`}` : n
}

function ChartTip({
  active,
  payload,
  label,
  unit
}: {
  active?: boolean
  payload?: Array<{ value: number; payload: { flag?: boolean } }>
  label?: string
  unit?: string
}) {
  if (!active || !payload?.length) return null
  const p = payload[0]
  return (
    <div className="rounded-[24px] border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
      <p className="text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="font-bold tabular-nums text-neutral-900 dark:text-white">
        {fmt(p.value, unit)}
        {p.payload.flag && <span className="ml-1.5 font-semibold text-red-600 dark:text-red-400">unusual</span>}
      </p>
    </div>
  )
}

export function DataWidget({ widget }: { widget: Widget }) {
  const flagged = widget.points?.filter((p) => p.flag) ?? []
  return (
    <div
      className={cn(
        'rounded-[24px] border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900',
        '[--dw-grid:#e5e5e5] [--dw-ink:#171717] [--dw-muted:#737373] dark:[--dw-grid:#262626] dark:[--dw-ink:#fafafa] dark:[--dw-muted:#a3a3a3]'
      )}
    >
      <p className="text-sm font-bold text-neutral-900 dark:text-white">{widget.title}</p>

      {widget.kind === 'stats' && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {widget.items?.map((item) => (
            <div key={item.label} className="rounded-[24px] bg-neutral-100 px-4 py-3 dark:bg-neutral-800">
              <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{item.label}</p>
              <p className={cn('mt-0.5 truncate text-xl font-bold tabular-nums', TONE[item.tone ?? 'neutral'])}>
                {item.value}
              </p>
              {item.hint && (
                <p className="truncate text-[11px] text-neutral-500 dark:text-neutral-400">{item.hint}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {(widget.kind === 'bar' || widget.kind === 'line') && widget.points && (
        <div className="mt-3 h-52 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {widget.kind === 'bar' ? (
              <BarChart data={widget.points} margin={{ top: 8, right: 4, bottom: 0, left: -12 }} barCategoryGap={2}>
                <CartesianGrid vertical={false} stroke="var(--dw-grid)" />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: 'var(--dw-muted)' }}
                  interval="preserveStartEnd"
                  minTickGap={12}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tick={{ fontSize: 11, fill: 'var(--dw-muted)' }}
                  tickFormatter={(v: number) => fmt(v, widget.unit)}
                />
                <Tooltip
                  cursor={{ fill: 'var(--dw-grid)', opacity: 0.5 }}
                  content={<ChartTip unit={widget.unit} />}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={36}>
                  {widget.points.map((p) => (
                    <Cell key={p.label} fill={p.flag ? FLAG : INK} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={widget.points} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid vertical={false} stroke="var(--dw-grid)" />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: 'var(--dw-muted)' }}
                  interval="preserveStartEnd"
                  minTickGap={12}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tick={{ fontSize: 11, fill: 'var(--dw-muted)' }}
                  tickFormatter={(v: number) => fmt(v, widget.unit)}
                />
                <Tooltip
                  cursor={{ stroke: 'var(--dw-muted)', strokeDasharray: '3 3' }}
                  content={<ChartTip unit={widget.unit} />}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={INK}
                  strokeWidth={2}
                  dot={(props: { cx?: number; cy?: number; payload?: { flag?: boolean }; index?: number }) =>
                    props.payload?.flag ? (
                      <circle
                        key={`dot-${props.index}`}
                        cx={props.cx}
                        cy={props.cy}
                        r={5}
                        fill={FLAG}
                        stroke="var(--dw-ink)"
                        strokeWidth={0}
                      />
                    ) : (
                      <circle key={`dot-${props.index}`} cx={props.cx} cy={props.cy} r={0} />
                    )
                  }
                  activeDot={{ r: 4, fill: INK }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {widget.kind === 'table' && widget.columns && widget.rows && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                {widget.columns.map((c) => (
                  <th key={c} className="px-2 py-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {widget.rows.map((row, i) => (
                <tr key={i} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                  {row.map((cell, j) => (
                    <td key={j} className="px-2 py-1.5 tabular-nums text-neutral-800 dark:text-neutral-200">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(widget.note || flagged.length > 0) && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-neutral-600 dark:text-neutral-300">
          {flagged.length > 0 && <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-red-500" />}
          <span>
            {widget.note}
            {flagged.length > 0 && !widget.note && `Unusual: ${flagged.map((p) => p.label).join(', ')}`}
          </span>
        </p>
      )}
    </div>
  )
}
